import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronLeft, ChevronRight, Plus, Calendar as CalendarIcon, Clock, MapPin,
  Users, X, Edit, Trash2, Check
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

import { API_URL } from '../../config/api';

const eventTypeOptions = [
  { value: 'meeting', label: 'Réunion', color: '#282E69' },
  { value: 'task', label: 'Tâche', color: '#E73B28' },
  { value: 'reminder', label: 'Rappel', color: '#F59E0B' },
  { value: 'other', label: 'Autre', color: '#6B7280' },
];

const DAYS = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
const MONTHS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

export default function CalendarPage() {
  const { user } = useOutletContext();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [assistants, setAssistants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('month'); // month or week
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    start_time: '',
    end_time: '',
    all_day: false,
    location: '',
    participants: [],
    event_type: 'meeting',
    color: '#282E69'
  });

  useEffect(() => {
    fetchEvents();
    fetchAssistants();
  }, [currentDate]);

  const fetchEvents = async () => {
    const token = localStorage.getItem('client_token');
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/client/calendar/events`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setEvents(data);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssistants = async () => {
    const token = localStorage.getItem('client_token');
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/client/users/${user.id}/assistants`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setAssistants(data);
      }
    } catch (error) {
      console.error('Error fetching assistants:', error);
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('client_token');

    try {
      const eventData = {
        ...newEvent,
        start_time: new Date(newEvent.start_time).toISOString(),
        end_time: new Date(newEvent.end_time).toISOString()
      };

      const response = await fetch(`${API_URL}/api/client/calendar/events`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(eventData)
      });

      if (response.ok) {
        toast.success('Événement créé avec succès');
        setShowCreateModal(false);
        resetNewEvent();
        fetchEvents();
      } else {
        toast.error('Erreur lors de la création');
      }
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!confirm('Es-tu sûr de vouloir supprimer cet événement ?')) return;
    const token = localStorage.getItem('client_token');

    try {
      const response = await fetch(`${API_URL}/api/client/calendar/events/${eventId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Événement supprimé');
        setShowEventModal(false);
        setSelectedEvent(null);
        fetchEvents();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const resetNewEvent = () => {
    setNewEvent({
      title: '',
      description: '',
      start_time: '',
      end_time: '',
      all_day: false,
      location: '',
      participants: [],
      event_type: 'meeting',
      color: '#282E69'
    });
  };

  const openCreateModal = (date = null) => {
    if (date) {
      const startTime = new Date(date);
      startTime.setHours(9, 0, 0, 0);
      const endTime = new Date(date);
      endTime.setHours(10, 0, 0, 0);
      
      setNewEvent(prev => ({
        ...prev,
        start_time: startTime.toISOString().slice(0, 16),
        end_time: endTime.toISOString().slice(0, 16)
      }));
    }
    setShowCreateModal(true);
  };

  const openEventModal = (event) => {
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    
    // Previous month days
    for (let i = startingDay - 1; i >= 0; i--) {
      const prevDate = new Date(year, month, -i);
      days.push({ date: prevDate, isCurrentMonth: false });
    }
    
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    
    // Next month days
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }
    
    return days;
  };

  const getEventsForDate = (date) => {
    return events.filter(event => {
      const eventDate = new Date(event.start_time);
      return eventDate.toDateString() === date.toDateString();
    });
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const isToday = (date) => {
    return date.toDateString() === new Date().toDateString();
  };

  const formatEventTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getEventTypeInfo = (type) => {
    return eventTypeOptions.find(t => t.value === type) || eventTypeOptions[3];
  };

  const calendarDays = getDaysInMonth(currentDate);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="calendar-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rm-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-rm-text">Agenda partagé</h1>
          <p className="text-rm-text-secondary">Planifiez tes rendez-vous avec ton équipe</p>
        </div>
        <Button
          onClick={() => openCreateModal()}
          className="bg-rm-primary hover:bg-rm-primary/90"
          data-testid="create-event-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          Nouvel événement
        </Button>
      </div>

      {/* Calendar Controls */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigateMonth(-1)}
              className="p-2 hover:bg-slate-100 rounded-lg"
              data-testid="prev-month"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h2 className="text-lg font-semibold text-rm-text min-w-[180px] text-center">
              {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h2>
            <button
              onClick={() => navigateMonth(1)}
              className="p-2 hover:bg-slate-100 rounded-lg"
              data-testid="next-month"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
          <Button variant="outline" size="sm" onClick={goToToday}>
            Aujourd'hui
          </Button>
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-px bg-slate-200 rounded-lg overflow-hidden">
          {/* Day headers */}
          {DAYS.map(day => (
            <div key={day} className="bg-slate-50 p-2 text-center text-sm font-medium text-rm-text-secondary">
              {day}
            </div>
          ))}
          
          {/* Calendar days */}
          {calendarDays.map((dayInfo, index) => {
            const dayEvents = getEventsForDate(dayInfo.date);
            const isCurrentDay = isToday(dayInfo.date);
            
            return (
              <div
                key={index}
                onClick={() => openCreateModal(dayInfo.date)}
                className={`bg-white min-h-[100px] p-2 cursor-pointer hover:bg-slate-50 transition-colors ${
                  !dayInfo.isCurrentMonth ? 'opacity-40' : ''
                }`}
                data-testid={`calendar-day-${dayInfo.date.getDate()}`}
              >
                <div className={`text-sm font-medium mb-1 w-7 h-7 flex items-center justify-center rounded-full ${
                  isCurrentDay ? 'bg-rm-primary text-white' : 'text-rm-text'
                }`}>
                  {dayInfo.date.getDate()}
                </div>
                <div className="space-y-1">
                  {dayEvents.slice(0, 3).map((event) => {
                    const typeInfo = getEventTypeInfo(event.event_type);
                    return (
                      <button
                        key={event.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          openEventModal(event);
                        }}
                        className="w-full text-left px-1.5 py-0.5 text-xs rounded truncate text-white"
                        style={{ backgroundColor: event.color || typeInfo.color }}
                      >
                        {event.title}
                      </button>
                    );
                  })}
                  {dayEvents.length > 3 && (
                    <p className="text-xs text-rm-text-secondary px-1">
                      +{dayEvents.length - 3} autre(s)
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Upcoming Events */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4">
        <h3 className="font-semibold text-rm-text mb-4 flex items-center gap-2">
          <CalendarIcon className="w-5 h-5 text-rm-primary" />
          Prochains événements
        </h3>
        <div className="space-y-3">
          {events
            .filter(e => new Date(e.start_time) >= new Date())
            .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
            .slice(0, 5)
            .map(event => {
              const typeInfo = getEventTypeInfo(event.event_type);
              return (
                <div
                  key={event.id}
                  onClick={() => openEventModal(event)}
                  className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
                >
                  <div 
                    className="w-1 h-12 rounded-full"
                    style={{ backgroundColor: event.color || typeInfo.color }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-rm-text truncate">{event.title}</p>
                    <p className="text-sm text-rm-text-secondary">
                      {new Date(event.start_time).toLocaleDateString('fr-FR', {
                        weekday: 'long',
                        day: 'numeric',
                        month: 'long'
                      })} à {formatEventTime(event.start_time)}
                    </p>
                  </div>
                  <span className="text-xs px-2 py-1 bg-white rounded-full text-rm-text-secondary">
                    {typeInfo.label}
                  </span>
                </div>
              );
            })}
          {events.filter(e => new Date(e.start_time) >= new Date()).length === 0 && (
            <p className="text-center py-8 text-rm-text-secondary">
              Aucun événement à venir
            </p>
          )}
        </div>
      </div>

      {/* Create Event Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl p-6 w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-rm-text">Nouvel événement</h2>
                <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleCreateEvent} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">Titre *</label>
                  <input
                    type="text"
                    required
                    value={newEvent.title}
                    onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                    placeholder="Réunion avec le client"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-rm-text mb-1">Début *</label>
                    <input
                      type="datetime-local"
                      required
                      value={newEvent.start_time}
                      onChange={(e) => setNewEvent({ ...newEvent, start_time: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-rm-text mb-1">Fin *</label>
                    <input
                      type="datetime-local"
                      required
                      value={newEvent.end_time}
                      onChange={(e) => setNewEvent({ ...newEvent, end_time: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-rm-text mb-1">Type</label>
                    <select
                      value={newEvent.event_type}
                      onChange={(e) => {
                        const typeInfo = getEventTypeInfo(e.target.value);
                        setNewEvent({ ...newEvent, event_type: e.target.value, color: typeInfo.color });
                      }}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                    >
                      {eventTypeOptions.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-rm-text mb-1">Couleur</label>
                    <input
                      type="color"
                      value={newEvent.color}
                      onChange={(e) => setNewEvent({ ...newEvent, color: e.target.value })}
                      className="w-full h-10 border border-slate-200 rounded-lg cursor-pointer"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">Lieu</label>
                  <input
                    type="text"
                    value={newEvent.location}
                    onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                    placeholder="Bureau, Visio, etc."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">Participants</label>
                  <select
                    multiple
                    value={newEvent.participants}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions, option => option.value);
                      setNewEvent({ ...newEvent, participants: selected });
                    }}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20 min-h-[80px]"
                  >
                    {assistants.map(assistant => (
                      <option key={assistant.id} value={assistant.id}>
                        {assistant.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-rm-text-secondary mt-1">
                    Maintenez Ctrl/Cmd pour sélectionner plusieurs participants
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">Description</label>
                  <textarea
                    value={newEvent.description}
                    onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                    rows={3}
                    placeholder="Détails de l'événement..."
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                    Annuler
                  </Button>
                  <Button type="submit" className="bg-rm-primary">
                    Créer l'événement
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Event Detail Modal */}
      <AnimatePresence>
        {showEventModal && selectedEvent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowEventModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header with color */}
              <div 
                className="p-6"
                style={{ backgroundColor: selectedEvent.color || '#282E69' }}
              >
                <div className="flex items-start justify-between">
                  <div className="text-white">
                    <p className="text-sm opacity-80">
                      {getEventTypeInfo(selectedEvent.event_type).label}
                    </p>
                    <h2 className="text-xl font-bold mt-1">{selectedEvent.title}</h2>
                  </div>
                  <button 
                    onClick={() => setShowEventModal(false)} 
                    className="p-1 hover:bg-white/20 rounded-lg text-white"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 space-y-4">
                <div className="flex items-center gap-3 text-rm-text-secondary">
                  <Clock className="w-5 h-5" />
                  <div>
                    <p className="font-medium text-rm-text">
                      {new Date(selectedEvent.start_time).toLocaleDateString('fr-FR', {
                        weekday: 'long',
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                    <p className="text-sm">
                      {formatEventTime(selectedEvent.start_time)} - {formatEventTime(selectedEvent.end_time)}
                    </p>
                  </div>
                </div>

                {selectedEvent.location && (
                  <div className="flex items-center gap-3 text-rm-text-secondary">
                    <MapPin className="w-5 h-5" />
                    <span>{selectedEvent.location}</span>
                  </div>
                )}

                {selectedEvent.description && (
                  <div className="pt-4 border-t border-slate-100">
                    <p className="text-sm text-rm-text">{selectedEvent.description}</p>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="p-4 border-t border-slate-100 flex justify-between">
                <Button
                  variant="outline"
                  onClick={() => handleDeleteEvent(selectedEvent.id)}
                  className="text-red-500 hover:text-red-600 hover:border-red-200"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Supprimer
                </Button>
                <Button onClick={() => setShowEventModal(false)}>
                  Fermer
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
